import { useAccountStore } from "@/modules/account/store"
import { useAppConfigStore } from "@/store/appConfig"
import { ModuleNameWithVisibilityCheck } from "@/logic/modules"
import { exhaustiveCheck } from "@/logic/tsHelpers"

export type AdminArea = Exclude<
  | ModuleNameWithVisibilityCheck
  | "categories"
  | "import"
  | "kee_credits"
  | "mails"
  | "pages"
  | "settings"
  | "stats_ratings"
  | "stats_training"
  | "stats_users"
  | "stats_quiz_challenge"
  | "stats_user_activity"
  | "stats_views"
  | "stats_wbt"
  | "tags"
  | "tag_groups"
  | "quiz_teams"
  | "users"
  | "user_roles"
  | "bin"
  | "stats_courses"
  | "course_assistant"
  | "integrations",
  "quiz"
>

export function useAdminAreaAccess() {
  const accountStore = useAccountStore()
  const appConfigStore = useAppConfigStore()

  const isVisibleForAdmin = (area: AdminArea): boolean => {
    switch (area) {
      case "advertisements":
        return appConfigStore.isModuleVisible("advertisements") && accountStore.rights["advertisements-edit"]
      case "appointments":
        return appConfigStore.isModuleVisible("appointments") && (accountStore.rights["appointments-edit"] || accountStore.rights["appointments-view"])
      case "categories":
        return appConfigStore.isModuleVisible("questions") && accountStore.rights["categories-edit"]
      case "communities":
        return appConfigStore.isModuleVisible("communities") && accountStore.rights["communities-edit"]
      case "competitions":
        return appConfigStore.isModuleVisible("competitions") && accountStore.rights["competitions-edit"]
      case "comments":
        return appConfigStore.isModuleVisible("comments") && accountStore.rights["comments-personaldata"]
      case "courses":
        return appConfigStore.isModuleVisible("courses") && (accountStore.rights["courses-edit"] || accountStore.rights["courses-view"])
      case "course_assistant":
        return !!appConfigStore.settings.module_course_assistant && isVisibleForAdmin("courses")
      case "forms":
        return appConfigStore.isModuleVisible("forms") && accountStore.rights["forms-edit"]
      case "kee_credits":
        return accountStore.isMainAdmin
      case "keywords":
        return appConfigStore.isModuleVisible("keywords") && accountStore.rights["keywords-edit"]
      case "import":
        return (!!appConfigStore.settings.import_questions && accountStore.rights["questions-edit"]) || (!!appConfigStore.settings.import_users && accountStore.rights["users-edit"]) || (!!appConfigStore.settings.import_users_delete && accountStore.rights["users-edit"])
      case "integrations":
        return accountStore.isMainAdmin
      case "learningmaterials":
        return appConfigStore.isModuleVisible("learningmaterials") && accountStore.rights["learningmaterials-edit"]
      case "mails":
        return accountStore.rights["mails-edit"]
      case "news":
        return appConfigStore.isModuleVisible("news") && accountStore.rights["news-edit"]
      case "pages":
        return accountStore.rights["pages-edit"]
      case "public_api":
        return appConfigStore.isModuleVisible("public_api") && accountStore.isMainAdmin
      case "settings":
        return accountStore.rights["settings-edit"]
      case "stats_ratings":
        return accountStore.rights["settings-ratings"]
      case "stats_training":
        return !appConfigStore.settings.hide_stats_training && accountStore.rights["questions-stats"]
      case "stats_quiz_challenge":
        return appConfigStore.isModuleVisible("quiz") && !appConfigStore.settings.hide_stats_quiz_challenge && accountStore.rights["questions-stats"]
      case "stats_user_activity":
        return accountStore.isSuperAdmin
      case "stats_users":
        return !appConfigStore.settings.hide_stats_users && accountStore.rights["users-stats"] && accountStore.rights["users-personaldata"] && !appConfigStore.settings.hide_personal_data
      case "stats_courses":
        return appConfigStore.isModuleVisible("courses") && accountStore.rights["courses-stats"]
      case "stats_views":
        return !appConfigStore.settings.hide_stats_views && accountStore.rights["settings-viewcounts"]
      case "stats_wbt":
        return !appConfigStore.settings.hide_stats_wbt && !!appConfigStore.settings.wbt_enabled && accountStore.rights["learningmaterials-stats"]
      case "tags":
        return accountStore.rights["tags-edit"]
      case "tag_groups":
        return accountStore.rights["tags-edit"] && !appConfigStore.settings.hide_tag_groups
      case "suggested_questions":
        return appConfigStore.isModuleVisible("suggested_questions") && accountStore.rights["suggestedquestions-edit"]
      case "tests":
        return appConfigStore.isModuleVisible("tests") && (accountStore.rights["tests-edit"] || accountStore.rights["tests-view"])
      case "questions":
        return appConfigStore.isModuleVisible("questions") && accountStore.rights["questions-edit"]
      case "quiz_teams":
        return appConfigStore.isModuleVisible("quiz") && accountStore.rights["quizteams-personaldata"]
      case "users":
        return accountStore.rights["users-edit"] || accountStore.rights["users-view"]
      case "user_roles":
        return accountStore.isMainAdmin
      case "vouchers":
        return appConfigStore.isModuleVisible("vouchers") && accountStore.rights["vouchers-edit"]
      case "webinars":
        return appConfigStore.isModuleVisible("webinars")
      case "tenants":
        return appConfigStore.isModuleVisible("tenants") && accountStore.isMainAdmin
      case "bin":
        return accountStore.rights["tags-edit"] || (appConfigStore.isModuleVisible("appointments") && accountStore.rights["appointments-edit"])
      case "certificates":
        return appConfigStore.isModuleVisible("certificates") && accountStore.rights["courses-certificatetemplates"]
      default:
        exhaustiveCheck(area)
    }
  }

  return {
    isVisibleForAdmin,
  }
}
